import { sortBy, uniqBy } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';

import {
  EmployeeCalendarEvent,
  EmployeeCalendarPersonWithLegalEntityId,
  getEmployeeCalendarPersonsFromEvents,
  useEmployeeAbsenceCalendarConfig,
} from '~/components/EmployeeCalendar';
import {
  useEmployeeCalendarPublicHolidays,
  useLimitedAbsenceCalendarLegend,
} from '~/components/EmployeeCalendar/Absences';
import { AbsenceStatus, AbsenceType } from '~/data/absences/types';
import { useAllEmployees } from '~/data/employees';
import {
  AbsenceHistoryLimited,
  AbsencesReportLimitedData,
} from '~/data/reports';
import { useShowEmployeesModeSelector } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/ShowEmployeesModeSelector/useShowEmployeesModeSelector';

type PropsType = {
  startDate: Date;
  endDate: Date;
  data: AbsencesReportLimitedData[];
};

export const useAbsenceCalendarLimitedReport = (props: PropsType) => {
  const { data, startDate, endDate } = props;

  const { getEmployeeById } = useAllEmployees();

  // Process absences
  const {
    absenceEntries,
    reportEmployees,
    absenceEmployees,
    hasNonAbsentEmployees,
    hasAbsentEmployees,
  } = useMemo(() => {
    const reportEmployees: EmployeeCalendarPersonWithLegalEntityId[] = [];

    const absenceEntries: EmployeeCalendarEvent[] = data
      .map((reportData) => {
        const {
          fEmpAbsenceShortHistory,
          fEmpAbsenceLongHistory,
          fEmpId,
          fEmpFirstName,
          fEmpLastName,
          fEmpLegalEntityId,
        } = reportData;

        const employeeData = getEmployeeById(fEmpId);

        reportEmployees.push({
          employeeId: fEmpId,
          firstName: fEmpFirstName,
          lastName: fEmpLastName,
          profilePic: employeeData?.fEmpProfPictureId,
          legalEntityId: fEmpLegalEntityId,
        });

        const getEvent = (
          absence: AbsenceHistoryLimited,
          type: AbsenceType,
        ): EmployeeCalendarEvent => {
          const {
            fEmpAbsenceEndDate,
            fEmpAbsenceStartDate,
            fEmpAbsenceUid,
            fEmpAbsenceStatus,
            fEmpAbsenceClassification,
          } = absence;

          return {
            uId: fEmpAbsenceUid,
            employeeId: fEmpId,
            firstName: fEmpFirstName,
            lastName: fEmpLastName,
            profilePic: employeeData?.fEmpProfPictureId,
            eventCategory: type,
            eventCode: fEmpAbsenceClassification,
            status: fEmpAbsenceStatus,
            startDate: moment(fEmpAbsenceStartDate).toDate(),
            endDate: !!fEmpAbsenceEndDate
              ? moment(fEmpAbsenceEndDate).toDate()
              : undefined,
          };
        };
        const processAbsences = (
          absences: AbsenceHistoryLimited[],
          type: AbsenceType,
        ) =>
          absences
            .filter(
              ({ fEmpAbsenceStatus }) =>
                fEmpAbsenceStatus !== AbsenceStatus.REJECTED,
            )
            .map((absence) => getEvent(absence, type));

        const shortAbsenceEvents = processAbsences(
          fEmpAbsenceShortHistory,
          AbsenceType.SHORT,
        );
        const longAbsenceEvents = processAbsences(
          fEmpAbsenceLongHistory,
          AbsenceType.LONG,
        );

        return shortAbsenceEvents.concat(longAbsenceEvents);
      })
      .flat();

    const sortedReportEmployees = sortBy(
      uniqBy(reportEmployees, (emp) => emp.employeeId),
      (emp) => emp.firstName,
    );

    const absenceEmployees =
      getEmployeeCalendarPersonsFromEvents(absenceEntries);

    const hasNonAbsentEmployees =
      reportEmployees.length > absenceEmployees.length;
    const hasAbsentEmployees = !!absenceEmployees.length;

    return {
      reportEmployees: sortedReportEmployees,
      absenceEntries,
      absenceEmployees,
      hasNonAbsentEmployees,
      hasAbsentEmployees,
    };
  }, [data, getEmployeeById]);

  const { publicHolidayEvents, isLoading: isLoadingPublicHolidays } =
    useEmployeeCalendarPublicHolidays({
      startDate,
      endDate,
      reportEmployees,
    });

  const { allEvents } = useMemo(() => {
    const allEvents = publicHolidayEvents.concat(absenceEntries);

    return {
      allEvents,
    };
  }, [absenceEntries, publicHolidayEvents]);

  const { eventColorLegends, getAbsenceLegend } =
    useLimitedAbsenceCalendarLegend({ events: allEvents });

  const calendarProps = useEmployeeAbsenceCalendarConfig({
    events: allEvents,
    eventColorLegends,
    getAbsenceLegend,
  });

  const showEmployeesModeSelector = useShowEmployeesModeSelector({
    hasAbsentEmployees,
    hasNonAbsentEmployees,
  });

  return {
    counts: {
      employees: reportEmployees.length,
      absences: absenceEntries.length,
    },
    showEmployeesModeSelector,
    calendarProps: {
      ...calendarProps,
      listEmployees:
        showEmployeesModeSelector.value === 'ALL'
          ? reportEmployees
          : absenceEmployees,
    },
    isLoading: isLoadingPublicHolidays,
  };
};
